import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  differenceInCalendarDays,
  format,
  getDaysInMonth,
  startOfMonth,
  subDays,
  subMonths,
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Activity, ChevronRight, TrendingDown, TrendingUp, X } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAppSystem } from '../../hooks/useAppSystem';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import { isAppInstalled } from '../../lib/controlCentreApps';
import { useControlCentreBannerMetrics } from '../../hooks/useControlCentreBannerMetrics';
import { useSalesCreatedTodayMetrics } from '../../hooks/useSalesCreatedTodayMetrics';
import { useNewLeadsTodayMetrics } from '../../hooks/useNewLeadsTodayMetrics';
import { useDailyProfitMonthBreakdown } from '../../hooks/useDailyProfitMonthBreakdown';
import { useCalendarSalesMetrics } from '../../hooks/useCalendarSalesMetrics';
import { usePaymentsByTypeThisMonth } from '../../hooks/usePaymentsByTypeThisMonth';
import { useAccountsReceivable } from '../../hooks/useAccountsReceivable';
import { useCrmLeadMetrics } from '../../hooks/useCrmLeadMetrics';
import { useInvoicedThisMonth } from '../../hooks/useControlCentreHeroExtras';
import { formatCurrency } from '../../lib/utils';
import { averageSale, salesCountLabel, salesUnitFor } from '../../lib/salesUnits';
import ActionsRequiredPanel from './ActionsRequiredPanel';
import ActivityLogPanel from './ActivityLogPanel';
import SalesMonthModal from './SalesMonthModal';
import CashCollectedMonthModal from './CashCollectedMonthModal';
import CrmLeadsMonthModal from './CrmLeadsMonthModal';
import DailyProfitModal from './DailyProfitModal';
import ActiveWorkersPanel from './ActiveWorkersPanel';
import ControlCentreReportCards from './ControlCentreReportCards';
import ControlCentreReportSearch from './ControlCentreReportSearch';
import { DashboardViewToggle } from './DashboardViewToggle';

const bannerFont =
  "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif";

const CRM_APP_SLUG = 'crm';

/** Real first name or nothing — no "Admin"/"there" filler in the greeting. */
function getWelcomeName(firstName?: string | null): string {
  return firstName?.trim() || '';
}

/** Month-over-month % change, or null when there's no baseline. */
function percentChange(current: number, previous: number): number | null {
  if (!previous) return null;
  return Math.round(((current - previous) / Math.abs(previous)) * 100);
}

/** Shopify-style trend: small arrow + muted green/red percentage. */
function TrendChip({ pct }: { pct: number | null }) {
  if (pct === null || pct === 0) return null;
  const up = pct > 0;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-0.5 text-[10px] sm:text-[12px] font-medium ${
        up ? 'text-green-700' : 'text-red-700'
      }`}
    >
      {up ? (
        <TrendingUp className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
      ) : (
        <TrendingDown className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
      )}
      {Math.abs(pct)}%
    </span>
  );
}

/** One white metric card in the hero's 2×2 grid (Shopify Analytics look). */
function HeroTile({
  label,
  value,
  suffix,
  trendPct,
  loading,
  onClick,
  children,
}: {
  label: string;
  value: string;
  /** Unit rendered after the value at a smaller size (e.g. "/day"), so the
   *  number itself never gets truncated on narrow two-up mobile tiles. */
  suffix?: string;
  trendPct?: number | null;
  loading?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`min-w-0 rounded-xl border border-gray-200 bg-white shadow-sm p-2.5 sm:p-4 text-left ${
        onClick ? 'transition-colors hover:bg-gray-50 active:scale-[0.995] touch-manipulation' : ''
      }`}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-[11px] sm:text-[13px] font-medium text-gray-700 truncate">
          {label}
        </span>
        {onClick ? (
          <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-400 shrink-0" aria-hidden />
        ) : null}
      </span>
      <span className="mt-1 flex items-baseline gap-1 sm:gap-2 min-w-0">
        <span
          className="text-[17px] sm:text-2xl font-semibold text-gray-900 leading-none tabular-nums truncate"
          style={{ letterSpacing: '-0.01em' }}
        >
          {loading ? '—' : value}
        </span>
        {!loading && suffix ? (
          <span className="shrink-0 text-[10px] sm:text-[13px] font-medium text-gray-500">
            {suffix}
          </span>
        ) : null}
        {!loading && trendPct !== undefined ? <TrendChip pct={trendPct} /> : null}
      </span>
      {children ? (
        <span className="mt-2 sm:mt-2.5 block space-y-0.5 sm:space-y-1">{children}</span>
      ) : null}
    </Tag>
  );
}

/**
 * Sales velocity — revenue per day the lead engine is producing.
 *
 *   leads per day  ×  avg deal value  ×  conversion rate
 *
 * This is the classic (opportunities × value × win rate) ÷ cycle formula with
 * the cycle algebraically cancelled out: when the opportunity count is the
 * pipeline that arrives in one cycle, the cycle length divides straight back
 * out. Dividing by cycle length was what produced absurd numbers for
 * same-day-closing businesses, and it was never needed for a per-day rate.
 *
 * Two deliberate input choices:
 *  • conversion = matured-cohort conversion (won ÷ created, 30–90 days back),
 *    not win rate — so leads that sit in the pipeline forever count against it.
 *  • avg deal = trailing 180-day average, not this month's handful of wins.
 */
function salesVelocityPerDay(
  leadsPerDay: number,
  avgDealValue: number,
  conversionPct: number
): number {
  if (leadsPerDay <= 0 || avgDealValue <= 0 || conversionPct <= 0) return 0;
  return leadsPerDay * avgDealValue * (conversionPct / 100);
}

function TileStat({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <span className="flex items-baseline justify-between gap-1.5 sm:gap-2 text-[10px] sm:text-[12px] leading-snug">
      <span className="text-gray-500 truncate">{label}</span>
      <span className={`font-medium tabular-nums shrink-0 ${valueClassName ?? 'text-gray-900'}`}>
        {value}
      </span>
    </span>
  );
}

export default function ControlCentreWelcomeBanner() {
  const { selectedBusiness } = useBusiness();
  const { coreApps, installedAddons, loading: appsLoading } = useAppSystem();
  const { currentResource, canAccess } = usePermissionCheck();
  const [now, setNow] = useState(() => new Date());
  const [salesCreatedModalOpen, setSalesCreatedModalOpen] = useState(false);
  const [cashCollectedModalOpen, setCashCollectedModalOpen] = useState(false);
  const [newLeadsModalOpen, setNewLeadsModalOpen] = useState(false);
  const [dailyProfitModalOpen, setDailyProfitModalOpen] = useState(false);
  const [activityLogOpen, setActivityLogOpen] = useState(false);

  const {
    days: dailyProfitDays,
    loading: dailyProfitLoading,
    load: loadDailyProfit,
  } = useDailyProfitMonthBreakdown();
  const {
    payrollTotal,
    periodLabel,
    loading: payrollLoading,
    showPayroll,
  } = useControlCentreBannerMetrics();
  const { salesFinishingToday, loading: salesTodayLoading } = useSalesCreatedTodayMetrics();
  const { newLeadsToday, loading: newLeadsTodayLoading } = useNewLeadsTodayMetrics();
  const {
    monthSales,
    monthSalesLastMonth,
    monthBookings,
    monthOrders,
    loading: monthSalesLoading,
  } = useCalendarSalesMetrics();
  // Legacy tiles only render in development; skip their queries elsewhere.
  const legacyBusinessId = import.meta.env.DEV ? selectedBusiness?.id : undefined;
  const {
    cashCollected: collectedThisMonth,
    creditRedeemed,
    loading: collectedLoading,
  } = usePaymentsByTypeThisMonth(legacyBusinessId);
  const { summary: arSummary, loading: arLoading } = useAccountsReceivable(legacyBusinessId);
  const { invoicedTotal, loading: invoicedLoading } = useInvoicedThisMonth();

  const isCrmInstalled =
    !appsLoading && isAppInstalled(CRM_APP_SLUG, coreApps, installedAddons);
  const showCrmTiles = isCrmInstalled && canAccess.crm;
  const crm = useCrmLeadMetrics();

  const showMoneyTiles = canAccess.calendarSales;
  const showDailyProfit = canAccess.calendarSales;

  useEffect(() => {
    if (!showDailyProfit) return;
    void loadDailyProfit();
    const interval = setInterval(() => void loadDailyProfit(), 60_000);
    return () => clearInterval(interval);
  }, [showDailyProfit, loadDailyProfit]);

  const todayProfit =
    dailyProfitDays.find((day) => day.date === format(new Date(), 'yyyy-MM-dd'))?.profit ?? 0;
  const yesterdayProfit =
    dailyProfitDays.find((day) => day.date === format(subDays(new Date(), 1), 'yyyy-MM-dd'))
      ?.profit ?? 0;
  const profitPct = percentChange(todayProfit, yesterdayProfit);

  const timezone = selectedBusiness?.timezone || 'America/Los_Angeles';

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(tick);
  }, []);

  const dateDisplay = useMemo(
    () => formatInTimeZone(now, timezone, 'EEEE, MMMM d, yyyy'),
    [now, timezone]
  );

  const welcomeName = getWelcomeName(currentResource?.first_name);
  const businessDisplayName = selectedBusiness?.name?.trim() || 'Your business';
  const businessLogoUrl =
    (selectedBusiness as any)?.booking_settings?.logo_url || null;

  const salesPct = percentChange(monthSales, monthSalesLastMonth);
  // Sales count and average span bookings AND counter-sale orders, so
  // order-only businesses aren't reported as zero jobs / $0 average.
  const salesUnit = salesUnitFor(monthBookings, monthOrders);
  const salesCount = monthBookings + monthOrders;
  const avgSale = averageSale(monthSales, monthBookings, monthOrders);
  const outstanding = arSummary?.totalUnpaidBalance ?? 0;
  const unpaidCount = arSummary?.unpaidInvoiceCount ?? 0;
  // Lead flow is a rate, so both months are measured per day: this month over
  // the days elapsed so far, last month over its full length.
  const daysElapsedThisMonth = differenceInCalendarDays(now, startOfMonth(now)) + 1;
  const daysInLastMonth = getDaysInMonth(subMonths(now, 1));
  const leadsPerDay = crm.newLeadsThisMonth / Math.max(daysElapsedThisMonth, 1);
  const leadsPerDayLastMonth = crm.newLeadsLastMonth / Math.max(daysInLastMonth, 1);
  const velocityNow = salesVelocityPerDay(
    leadsPerDay,
    crm.avgWonDealSizeTrailing,
    crm.maturedConversionRate
  );
  const velocityLastMonth = salesVelocityPerDay(
    leadsPerDayLastMonth,
    crm.avgWonDealSizeTrailing,
    crm.maturedConversionRate
  );
  const velocityPct = percentChange(velocityNow, velocityLastMonth);

  return (
    <section
      className="relative pt-3 sm:pt-4 mb-4 lg:mb-6"
      style={{ fontFamily: bannerFont }}
      aria-label="Welcome banner"
    >
      {/* Header row: identity left, Activity top-right — spans the full hero. */}
      <div className="flex flex-wrap items-center justify-between gap-3 min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          {businessLogoUrl && (
            <img
              src={businessLogoUrl}
              alt=""
              className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl object-contain bg-white border border-gray-200 shadow-sm shrink-0"
            />
          )}
          <div className="min-w-0">
            <h1
              className="text-[24px] font-bold text-gray-900 leading-[1.15] sm:text-[28px] truncate"
              style={{ letterSpacing: '-0.02em' }}
            >
              {businessDisplayName}
            </h1>
            <p className="text-[12px] sm:text-[13px] leading-tight truncate mt-0.5 font-medium text-gray-700">
              {welcomeName ? `Welcome back, ${welcomeName}` : 'Welcome back'}
            </p>
            {/* Own line so the full date shows instead of truncating on mobile. */}
            <p className="text-[12px] sm:text-[13px] leading-tight text-gray-500">
              {dateDisplay}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <DashboardViewToggle />
          <button
            type="button"
            onClick={() => setActivityLogOpen(true)}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 active:scale-[0.98] touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Activity className="h-4 w-4 text-gray-500" aria-hidden />
            Activity
          </button>
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-4">
        {/* ── Metric cards: 2×2 on tablet, one row of four on wide screens ── */}
        <div className="min-w-0 flex flex-col">
          {(showDailyProfit || showPayroll) ? (
            <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-gray-200 pb-3">
              {showDailyProfit ? (
                <button
                  type="button"
                  onClick={() => setDailyProfitModalOpen(true)}
                  className="flex items-baseline gap-2 text-[13px] text-gray-500 hover:text-gray-700 transition-colors touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <span>Daily profit</span>
                  <span className="font-semibold text-gray-900 tabular-nums">
                    {dailyProfitLoading ? '—' : formatCurrency(todayProfit)}
                  </span>
                  {!dailyProfitLoading ? <TrendChip pct={profitPct} /> : null}
                  {!dailyProfitLoading && profitPct !== null ? (
                    <span className="text-[12px] text-gray-400">yesterday</span>
                  ) : null}
                </button>
              ) : null}
              {showDailyProfit && showPayroll ? (
                <span className="hidden sm:block h-4 w-px bg-gray-200" aria-hidden />
              ) : null}
              {showPayroll ? (
                <a
                  href="/payroll"
                  className="flex items-baseline gap-2 text-[13px] text-gray-500 hover:text-gray-700 transition-colors touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <span>Payroll</span>
                  <span className="font-semibold text-gray-900 tabular-nums">
                    {payrollLoading ? '—' : formatCurrency(payrollTotal)}
                  </span>
                  {periodLabel ? (
                    <span className="text-[12px] text-gray-400">{periodLabel}</span>
                  ) : null}
                </a>
              ) : null}
            </div>
          ) : null}
          <div className="mb-3 empty:mb-0">
            <ActiveWorkersPanel layout="horizontal" hideWhenEmpty openMapOnClick />
          </div>
          {showMoneyTiles ? <ControlCentreReportSearch /> : null}
          {(showMoneyTiles || showCrmTiles) ? (
            <ControlCentreReportCards money={showMoneyTiles} crm={showCrmTiles} />
          ) : null}
          {/* Old tiles on the legacy queries, kept in development to compare against the report cards. */}
          {import.meta.env.DEV && (showMoneyTiles || showCrmTiles) ? (
            <section aria-label="Development legacy metrics" className="mt-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">
                Development · old cards
              </p>
            <div
              className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4"
            >
              {showMoneyTiles ? (
                <HeroTile
                  label="Sales this month"
                  value={formatCurrency(monthSales)}
                  trendPct={salesPct}
                  loading={monthSalesLoading}
                  onClick={() => setSalesCreatedModalOpen(true)}
                >
                  <TileStat
                    label="Today"
                    value={salesTodayLoading ? '—' : formatCurrency(salesFinishingToday)}
                  />
                  <TileStat
                    label={salesCountLabel(salesUnit)}
                    value={monthSalesLoading ? '—' : String(salesCount)}
                  />
                  <TileStat
                    label="Avg sale"
                    value={monthSalesLoading ? '—' : formatCurrency(avgSale)}
                  />
                </HeroTile>
              ) : null}

              {showMoneyTiles ? (
                <HeroTile
                  label="Cash collected"
                  value={formatCurrency(collectedThisMonth)}
                  loading={collectedLoading}
                  onClick={() => setCashCollectedModalOpen(true)}
                >
                  {creditRedeemed > 0 ? (
                    <TileStat
                      label="Credit redeemed"
                      value={formatCurrency(creditRedeemed)}
                      valueClassName="text-teal-600"
                    />
                  ) : null}
                  <TileStat
                    label="Invoiced this month"
                    value={invoicedLoading ? '—' : formatCurrency(invoicedTotal)}
                  />
                  <TileStat
                    label="Outstanding"
                    value={arLoading ? '—' : formatCurrency(outstanding)}
                    valueClassName="text-orange-600"
                  />
                  <TileStat
                    label="Unpaid invoices"
                    value={arLoading ? '—' : String(unpaidCount)}
                    valueClassName={unpaidCount > 0 ? 'text-red-600' : 'text-gray-900'}
                  />
                </HeroTile>
              ) : null}

              {showCrmTiles ? (
                <HeroTile
                  label="New leads this month"
                  value={String(crm.newLeadsThisMonth)}
                  trendPct={percentChange(crm.newLeadsThisMonth, crm.newLeadsLastMonth)}
                  loading={crm.loading}
                  onClick={() => setNewLeadsModalOpen(true)}
                >
                  <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 sm:gap-x-2.5 text-[10px] sm:text-[12px] leading-snug tabular-nums">
                    <span className="text-green-700 font-medium">{crm.openLeads} open</span>
                    <span className="text-gray-900 font-medium">{crm.leadsWonThisMonth} won</span>
                    <span className="text-red-600 font-medium">{crm.leadsLostThisMonth} lost</span>
                  </span>
                  <TileStat
                    label="Opening balance"
                    value={crm.loading ? '—' : String(crm.openingBalanceLeads)}
                    valueClassName="text-blue-600"
                  />
                  <TileStat
                    label="Today"
                    value={newLeadsTodayLoading ? '—' : String(newLeadsToday)}
                  />
                </HeroTile>
              ) : null}

              {showCrmTiles ? (
                <HeroTile
                  label="Sales velocity"
                  value={formatCurrency(velocityNow)}
                  suffix="/day"
                  trendPct={velocityPct}
                  loading={crm.loading}
                >
                  <TileStat
                    label="Leads per day"
                    value={crm.loading ? '—' : leadsPerDay.toFixed(1)}
                  />
                  <TileStat
                    label="Avg deal"
                    value={crm.loading ? '—' : formatCurrency(crm.avgWonDealSizeTrailing)}
                  />
                  <TileStat
                    label="Conversion"
                    value={crm.loading ? '—' : `${Math.round(crm.maturedConversionRate)}%`}
                  />
                </HeroTile>
              ) : null}
            </div>
            </section>
          ) : null}
        </div>

        {/* ── Needs-your-attention: two inboxes side by side ────────── */}
        <ActionsRequiredPanel variant="hero" />
      </div>

      <SalesMonthModal
        isOpen={salesCreatedModalOpen}
        onClose={() => setSalesCreatedModalOpen(false)}
      />
      <CashCollectedMonthModal
        isOpen={cashCollectedModalOpen}
        onClose={() => setCashCollectedModalOpen(false)}
        arSummary={arSummary}
      />
      <CrmLeadsMonthModal
        isOpen={newLeadsModalOpen}
        onClose={() => setNewLeadsModalOpen(false)}
        metrics={crm}
      />
      <DailyProfitModal
        isOpen={dailyProfitModalOpen}
        onClose={() => setDailyProfitModalOpen(false)}
      />

      {/* Full-screen activity log (replaces the old right-edge slide-out rail). */}
      {activityLogOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[100002] flex flex-col bg-white"
            style={{
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              fontFamily: bannerFont,
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Activity log"
          >
            <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="flex items-center gap-2.5 min-w-0">
                <Activity className="h-5 w-5 text-gray-500" aria-hidden />
                <h2 className="text-[17px] font-bold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                  Activity
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setActivityLogOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
                aria-label="Close activity log"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <ActivityLogPanel hideHeader className="h-full rounded-none border-0 shadow-none" />
            </div>
          </div>,
          document.body
        )}
    </section>
  );
}
